import { DocumentType, ShippingDocumentKind } from "generated-graphql/graphql";

export interface DrawerSections {
  isBillOfLading: boolean;
  /** What the paper is called in the heading and the data card title. */
  paperName: "Manifest" | "Bill of lading";
  /** Label for the number printed on the paper. */
  numberLabel: "Tracking number" | "Document number";
  /** Label and tooltip of the title pencil that edits the paper's data. */
  editDataLabel: "Edit Manifest Data" | "Edit bill of lading data";
  /** Summary lines about e-Manifest status, compliance escalation and certification. */
  showEManifestSummary: boolean;
  /** Warning that the manifest may be missing from e-Manifest. */
  showEManifestSiteAccessWarning: boolean;
  /** The Discrepancies and Compliance cards, which compare e-Manifest to other sources. */
  showDiscrepancyAndComplianceCards: boolean;
  /** The compare-data-sources action, which needs an e-Manifest to compare against. */
  showCompareSources: boolean;
  /** Review, process, replace and upload actions for an e-Manifest paper document. */
  showManifestDocumentActions: boolean;
  /** Info tooltip beside the Documents section title. */
  documentsInfoTooltip: string;
}

const MANIFEST_DOCUMENTS_INFO =
  "Only one document can be the active manifest document data source at a time. " +
  "Additional manifest copies and other associated documents (proof of vendor contact, " +
  "exception reports, etc.) can still be uploaded and attached.";

const BILL_OF_LADING_DOCUMENTS_INFO =
  "Only one document can be the bill of lading's data source at a time. " +
  "Other documents can still be uploaded and attached.";

/**
 * Which parts of the manifest drawer apply to a paper. A bill of lading is a
 * processed manifest tagged BILL_OF_LADING, but it is never filed in
 * e-Manifest, so everything that reads or compares e-Manifest data is hidden.
 */
export function billOfLadingDrawerSections(
  documentKind: ShippingDocumentKind | null | undefined
): DrawerSections {
  const isBillOfLading = documentKind === ShippingDocumentKind.BillOfLading;
  const showEManifestParts = !isBillOfLading;
  return {
    isBillOfLading,
    paperName: isBillOfLading ? "Bill of lading" : "Manifest",
    numberLabel: isBillOfLading ? "Document number" : "Tracking number",
    editDataLabel: isBillOfLading ? "Edit bill of lading data" : "Edit Manifest Data",
    showEManifestSummary: showEManifestParts,
    showEManifestSiteAccessWarning: showEManifestParts,
    showDiscrepancyAndComplianceCards: showEManifestParts,
    showCompareSources: showEManifestParts,
    showManifestDocumentActions: showEManifestParts,
    documentsInfoTooltip: isBillOfLading ? BILL_OF_LADING_DOCUMENTS_INFO : MANIFEST_DOCUMENTS_INFO,
  };
}

/**
 * The edit button reopens the bill's own upload in the review dialog, which only
 * works for a bill-typed source document on a tenant with container tracking.
 */
export function canEditBillOfLading({
  isBillOfLading,
  sourceDocumentType,
  containerTrackingEnabled,
}: {
  isBillOfLading: boolean;
  sourceDocumentType: DocumentType | null | undefined;
  containerTrackingEnabled: boolean;
}): boolean {
  return (
    isBillOfLading &&
    sourceDocumentType === DocumentType.WasteShippingDocument &&
    containerTrackingEnabled
  );
}
